"use client";

import { useGeo } from "@/i18n/routing";
import type { BaseSelectionResponse } from "@frend-digital/centra-types/selection";
import { useSelection, useToken } from "@frend-digital/centra/client";
import { depictAdapter, getDPC } from "@frend-digital/events/depict";
import { CustomAdapter, EventSubscription } from "@frend-digital/events/client";
import {
	centraGTMAdapter,
	type BeforeEvent,
	sendGTMEvent,
	type CentraGTMFormatter,
	selectionItemToGTAGProduct,
	type GA4Product,
} from "@frend-digital/events/gtm";
import { getLang } from "@frend-digital/next/i18n";
import { useParams } from "next/navigation";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { klaviyoAddToCart, klaviyoAddPurchaseData } from "./klaviyoEvents";
import { useLocale } from "@/i18n/client";
import { env } from "@/env.mjs";
import { useQuery } from "@tanstack/react-query";

type RawVoucher = {
	description?: string;
	priceOffAsNumber?: number;
	lines?: string[];
	voucher?: string;
};

type RawDiscount = { priceOffAsNumber?: number };

type RawItem = {
	totalPriceBeforeDiscountAsNumber?: number;
	totalPriceAsNumber?: number;
	campaign?: { name?: string };
	discounts?: {
		vouchers?: RawDiscount[];
	};
};

type RawOrder = {
	items?: RawItem[];
	discounts?: { vouchers?: RawVoucher[] };
	address?: {
		email?: string;
		firstName?: string;
		lastName?: string;
		address1?: string;
		zipCode?: string;
		city?: string;
		state?: string;
		country?: string;
		phoneNumber?: string;
	};
};

let _capturedOrder: RawOrder | null = null;

const createOrderCaptureAdapter = () =>
	new CustomAdapter((e) => {
		if (e.event === "purchase") {
			_capturedOrder = e.data as RawOrder;
		}
	});

// ─── Discount breakdown ───────────────────────────────────────────────────────

interface DiscountBreakdown {
	/** Sale/outlet price reductions from campaigns and price-list markdowns. */
	campaign_discount: number;
	/** Names of the campaigns applied — use in GTM to split sale vs outlet. */
	campaign_names: string[];
	/** Discount from non-loyalty voucher/promo codes. */
	discount_code_discount: number;
	/** The actual codes that were applied. */
	discount_codes: string[];
	/** Discount from loyalty bonus points. */
	loyalty_discount: number;
}

const buildDiscountBreakdown = (order: RawOrder): DiscountBreakdown => {
	const items: RawItem[] = order?.items ?? [];
	const orderVouchers: RawVoucher[] = order?.discounts?.vouchers ?? [];

	const isLoyalty = (v: RawVoucher) => v.description?.toLowerCase().includes("loyalty") ?? false;

	const loyalty_discount = orderVouchers
		.filter(isLoyalty)
		.reduce((sum, v) => sum + Math.abs(v.priceOffAsNumber ?? 0), 0);

	const nonLoyaltyVouchers = orderVouchers.filter((v) => !isLoyalty(v));
	const discount_codes: string[] = nonLoyaltyVouchers
		.map((v) => v.voucher as string)
		.filter(Boolean);

	const orderLevelCodeDiscount = nonLoyaltyVouchers
		.filter((v) => !v.lines || v.lines.length === 0)
		.reduce((sum, v) => sum + Math.abs(v.priceOffAsNumber ?? 0), 0);

	let campaign_discount = 0;
	let itemLevelCodeDiscount = 0;
	const campaignNameSet = new Set<string>();

	for (const item of items) {
		const itemVoucherDiscount = (item.discounts?.vouchers ?? []).reduce(
			(sum, v) => sum + Math.abs(v.priceOffAsNumber ?? 0),
			0,
		);
		const itemPriceReduction =
			(item.totalPriceBeforeDiscountAsNumber ?? 0) - (item.totalPriceAsNumber ?? 0);
		campaign_discount += Math.max(0, itemPriceReduction - itemVoucherDiscount);
		itemLevelCodeDiscount += itemVoucherDiscount;
		if (typeof item.campaign?.name === "string") {
			campaignNameSet.add(item.campaign.name);
		}
	}

	const discount_code_discount = orderLevelCodeDiscount + itemLevelCodeDiscount;

	return {
		campaign_discount: Math.round(campaign_discount * 100) / 100,
		campaign_names: Array.from(campaignNameSet),
		discount_code_discount: Math.round(discount_code_discount * 100) / 100,
		discount_codes,
		loyalty_discount: Math.round(loyalty_discount * 100) / 100,
	};
};

export type KlaviyoItem = {
	Name: string;
	ProductId: string;
	Price: number;
	ImageURL: string;
	URL: string;
	SKU: string;
};

export const useBeforeGtmEvent = () => {
	const { data: centra } = useSelection();

	const locale = useLocale();

	// Create a stable reference to Centra
	const centraRef = useRef<BaseSelectionResponse | undefined>(centra);
	centraRef.current = centra;

	// Add custom data to event parameters
	const beforeGtmEvent: BeforeEvent = useCallback(
		async (e) => {
			// For purchase events, use the address from the captured order — the
			// selection is already cleared when this event fires. For all other
			// events, read from the active selection.
			const address =
				e.event === "purchase"
					? _capturedOrder?.address
					: centraRef.current?.selection?.address;
			const user = centraRef.current?.loggedIn;

			// Delay is set to push the event right after Centra gets forceUpdated.
			await delay(10);

			if (e.event === "purchase") {
				await delay(2000);
			}

			// Enrich with userdata if exists

			const userData: UserData = {};

			const email = address?.email || user?.email;
			const phoneNumber = address?.phoneNumber;
			const firstName = address?.firstName || user?.firstName;
			const lastName = address?.lastName || user?.lastName;
			const address1 = address?.address1 || user?.address1;
			const city = address?.city || user?.city;
			const state = address?.state || user?.state;
			const country = address?.country || user?.country;
			const zipCode = address?.zipCode || user?.zipCode;

			if (email) {
				userData.UserData ??= {};
				userData.UserData.email = email;
			}

			if (phoneNumber) {
				userData.UserData ??= {};
				userData.UserData.phone_number = phoneNumber;
			}

			if (firstName) {
				userData.UserData ??= {};
				userData.UserData.address ??= {};
				userData.UserData.address.first_name = firstName;
			}
			if (lastName) {
				userData.UserData ??= {};
				userData.UserData.address ??= {};
				userData.UserData.address.last_name = lastName;
			}

			if (address1) {
				userData.UserData ??= {};
				userData.UserData.address ??= {};
				userData.UserData.address.street = address1;
			}
			if (city) {
				userData.UserData ??= {};
				userData.UserData.address ??= {};
				userData.UserData.address.city = city;
			}
			if (state) {
				userData.UserData ??= {};
				userData.UserData.address ??= {};
				userData.UserData.address.region = state;
			}
			if (country) {
				userData.UserData ??= {};
				userData.UserData.address ??= {};
				userData.UserData.address.country = country;
			}
			if (zipCode) {
				userData.UserData ??= {};
				userData.UserData.address ??= {};
				userData.UserData.address.postal_code = zipCode;
			}

			const discountData =
				e.event === "purchase" && _capturedOrder
					? { discount_breakdown: buildDiscountBreakdown(_capturedOrder) }
					: {};

			const result = {
				...e,
				...userData,
				...discountData,
			};

			const resultWithKlaviyo = {
				...result,
				...klaviyoAddToCart(result, centraRef, {
					locale,
				}),
				...klaviyoAddPurchaseData(result, centraRef),
			};

			return resultWithKlaviyo;
		},
		[locale],
	);

	return centra ? beforeGtmEvent : undefined;
};

const logAdapter = () =>
	new CustomAdapter((_e) => {
		if (process.env.NODE_ENV === "development") {
			// console.log(_e.event, _e.data);
		}
	});

const formatter: CentraGTMFormatter = (item) => {
	const returnItem: GA4Product = (() => {
		try {
			//! DO NOT REMOVE - weird way to get around sizing for Est Populo marketing agency
			if (item.size && item.sku && item.product?.productSku) {
				const split = item.sku.split("-");
				if (split[split.length - 1] === item.size) {
					const partialSku = `-${split[split.length - 2]}-${split[split.length - 1]}`;
					return {
						...selectionItemToGTAGProduct(item),
						item_id: item.product.productSku + partialSku,
					};
				}
			}

			return {
				...selectionItemToGTAGProduct(item),
				item_id: item.product?.productSku || item.sku!,
			};
		} catch (error) {
			return {
				...selectionItemToGTAGProduct(item),
				item_id: item.product?.productSku || item.sku!,
			};
		}
	})();

	try {
		return {
			...returnItem,
			coupon: item.discounts?.vouchers?.map((v) => v.voucher).join(",") || undefined,
			price:
				(item.priceEachBeforeDiscountAsNumber || 0) -
				(item.discounts?.totalDiscountAsNumber || 0) / (item.quantity || 1),
			discount: (item.discounts?.totalDiscountAsNumber || 0) / (item.quantity || 1) || undefined,
			campaign: item.campaign?.name || undefined,
		} as GA4Product;
	} catch (error) {
		return returnItem;
	}
};

/** Custom event parameters provided by Est Populo */
interface UserData {
	UserData?: {
		email?: string;
		phone_number?: string;
		address?: {
			first_name?: string;
			last_name?: string;
			street?: string;
			city?: string;
			region?: string;
			country?: string;
			postal_code?: string;
		};
	};
}

export const useDPC = () => {
	const { country } = useGeo();
	const token = useToken();
	return useQuery({
		queryKey: ["DPC", token, country],
		queryFn: async () => {
			const dpc = await getDPC(env.NEXT_PUBLIC_DEPICT_MERCHANT, {
				auto_observe: true,
				market: country.toLowerCase(),
			});

			dpc.setSessionId(token);

			return dpc;
		},
		enabled: !!token,
	});
};

const Events = () => {
	const { data: centra } = useSelection();
	const { country } = useGeo();
	const { geo } = useParams<{ geo: string }>();

	useEffect(() => {
		const language = getLang(geo);
		sendGTMEvent({
			event: "language",
			language,
		});
	}, [geo]);

	useEffect(() => {
		if (centra?.token) {
			sendGTMEvent({
				event: "userId",
				userId: centra.token,
			});
		}
	}, [centra?.token]);

	const beforeGtmEvent = useBeforeGtmEvent();

	const dpc = useDPC();

	const { data: selection } = useSelection();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const adapters = useMemo(() => {
		const adapters = [
			// Must be before centraGTMAdapter so _capturedOrder is set before
			// beforeGtmEvent reads it (the 2000ms delay gives plenty of time)
			createOrderCaptureAdapter(),
			centraGTMAdapter({ beforeEvent: beforeGtmEvent, formatter, selection }),
			logAdapter(),
			depictAdapter(env.NEXT_PUBLIC_DEPICT_MERCHANT, {
				market: country.toLowerCase(),
				auto_observe: true,
			}),
		];

		return adapters;
	}, [beforeGtmEvent, dpc, selection]);

	return <EventSubscription adapters={adapters} />;
};

export default Events;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
